import { type FC, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import type { IncidentType } from '@/types';

import IncidentListStickyContent from '@/components/Incidents/incident-list-sticky-content';
import IncidentSummary from '@/components/Incidents/incident-summary';
import IncidentListsLoader from '@/components/shared/loader/incident-lists-loader';
import StatusMessage from '@/components/shared/status-message';
import { ScrollArea } from '@/components/ui/scroll-area';
import useMobileLandscape from '@/hooks/use-mobile-landscape';
import { ERROR_FEEDBACK } from '@/utils/constants';

interface IncidentListRendererProps {
  data: IncidentType[];
  isLoading: boolean;
  isEmpty?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  activeFilterCount?: number;
  onShowFilter?: () => void;
  children?: ReactNode;
}

const IncidentListRenderer: FC<IncidentListRendererProps> = ({
  data,
  isLoading,
  isEmpty = false,
  isError = false,
  onRetry,
  activeFilterCount = 0,
  onShowFilter = () => {},
  children,
}) => {
  const { t } = useTranslation();
  const isMobileLandscape = useMobileLandscape();

  const stickyContent = (
    <IncidentListStickyContent filterCount={activeFilterCount} onShowFilter={onShowFilter} />
  );

  const getContent = () => {
    if (isLoading) {
      return <IncidentListsLoader />;
    }

    if (isError) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center py-8">
          <StatusMessage
            type="error"
            title={t(ERROR_FEEDBACK.general)}
            description={t(ERROR_FEEDBACK.incidents.api)}
            showReloadButton
            onRetry={onRetry}
          />
        </div>
      );
    }

    if (isEmpty) {
      return (
        <div className="min-h-[60vh] flex items-center justify-center py-8">
          <StatusMessage
            type="empty"
            title={t(ERROR_FEEDBACK.incidents.empty)}
            description={t(ERROR_FEEDBACK.incidents.search)}
          />
        </div>
      );
    }

    return (
      <div className="[&>*:not(:last-child)]:border-b">
        {data.map((incident) => (
          <IncidentSummary key={incident.gtfsAlertId} incident={incident} />
        ))}
      </div>
    );
  };

  const shouldShowChildren = !isLoading && !isError;

  if (isMobileLandscape) {
    return (
      <ScrollArea className="h-full" scrollBarClassName={isLoading ? 'hidden' : ''}>
        <div className="px-4">
          <div className="pb-4">{stickyContent}</div>
          <div>
            {getContent()}
            {shouldShowChildren && children}
          </div>
        </div>
      </ScrollArea>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="shrink-0 px-4 pb-4">{stickyContent}</div>
      <ScrollArea className="flex-1 min-h-0" scrollBarClassName={isLoading ? 'hidden' : ''}>
        <div className="px-4">
          {getContent()}
          {shouldShowChildren && children}
        </div>
      </ScrollArea>
    </div>
  );
};

export default IncidentListRenderer;
